/**
 * Method group extracted from the Konva map Stimulus controller.
 * These functions intentionally use `this` so the thin Stimulus adapter can
 * keep the existing target/value/action API while the implementation lives in
 * smaller feature-oriented modules.
 */
import Konva from 'konva';
import { groupedLayers, flattenLayers } from './layer_order.js';
import { mapAngleToBearing } from '../direction_dial.js';
import Sortable from 'sortablejs';
import { footprintContainsPoint, isDirectionalEffect } from './area_effect_geometry.js';
import {
  DRAW_WIDTH,
  ERASE_RADIUS,
  MAX_FOG_BRUSH_SIZE,
  MIN_FOG_BRUSH_SIZE,
  MAX_SCALE,
  MIN_SCALE,
  MIN_SHAPE_DIMENSION,
  POSITION_ZOOM_STEP,
  SHAPE_COLORS,
  SHAPE_DEFAULTS,
  SHAPE_TRANSFORMER_OPTIONS,
  TOOLBAR_DEFAULT_TOOLS,
  TOOLBAR_TOOLS,
  TOOLBAR_URL_PARAM,
  ZOOM_STEP
} from './constants.js';

export const ShapeMethods = {
  _canEditShape(shape) {
    if (!shape || this.readonlyValue) return false;
    if (!this.playerValue) return true;
    return shape.playerEditorIds
      ? shape.playerEditorIds.includes(this.userIdValue)
      : shape.creatorId === this.userIdValue;
  },

  _editableShapes() {
    return this._shapes.filter((shape) => this._canEditShape(shape));
  },
  _findShapeAtPoint(worldPos, { cycle = false } = {}) {
    const hits = this._shapes.filter((shape) => {
      if (!this._canEditShape(shape)) return false;
      const r = shape.konvaShape;
      const local = r.getTransform().copy().invert().point(worldPos);
      if (this._selectedShapeId != null && shape.id === this._selectedShapeId && isDirectionalEffect(shape)) {
        const halfCell = Math.max(
          r.getAttr('tileFootprint').size / 2,
          this._usingTouch ? 24 / this._stage.scaleX() : 0
        );
        if (Math.abs(local.x) <= halfCell && Math.abs(local.y) <= halfCell) return true;
      }
      if (shape.type === 'character')
        return Math.hypot(local.x - r.width() / 2, local.y - r.height() / 2) <= r.width() / 2;
      const footprint = r.getAttr('tileFootprint');
      return footprint
        ? footprintContainsPoint(footprint, local) ||
            (Math.abs(local.x) <= footprint.size / 2 && Math.abs(local.y) <= footprint.size / 2)
        : local.x >= 0 && local.x <= r.width() && local.y >= 0 && local.y <= r.height();
    });
    // A linked group is one canvas target. Its character is always on top;
    // individual effects remain editable through their explicit layer rows.
    if (hits.some((shape) => shape.parentId || shape.type === 'character')) {
      const top = hits.at(-1);
      const parent = top.parentId && this._findShapeById(top.parentId);
      return this._canEditShape(parent) ? parent : top;
    }
    const selectedIndex =
      this._selectedShapeId == null ? -1 : hits.findIndex((shape) => shape.id === this._selectedShapeId);
    if (selectedIndex !== -1) {
      // Keep dragging the selected layer, but let
      // completed clicks walk backwards through the saved stack and wrap around.
      return hits[cycle ? (selectedIndex + hits.length - 1) % hits.length : selectedIndex];
    }
    return hits.at(-1) || null;
  },

  _selectShape(id, { showControls = true } = {}) {
    if (!this._canEditShape(this._findShapeById(id))) return;
    this._shapePopoverCollapsed = !showControls;
    if (showControls) this._openMobileLayerPanel?.();
    else if (this._mobileEditor) this._collapseMobilePanels();
    if (this._selectedShapeId === id) {
      this._refreshShapePopover?.();
      return;
    }
    this._deselectShape();
    const shape = this._findShapeById(id);
    if (!shape) return;
    this._selectedShapeId = id;
    shape.konvaShape.dash([8, 4]);
    if (isDirectionalEffect(shape)) shape.konvaShape.setAttr('sourceSelected', true);
    // Keep the saved layer order; transformer handles live above the fog.
    this._ensureShapeOnTop(shape);
    if (this._shapeTransformer) {
      const noHandles = shape.type === 'character' || isDirectionalEffect(shape);
      this._shapeTransformer.resizeEnabled(!['area-effect', 'character'].includes(shape.type));
      this._shapeTransformer.nodes(noHandles ? [] : [shape.konvaShape]);
      this._shapeTransformer.visible(!noHandles);
    }
    this._shapesLayerAbove.batchDraw();
    this._shapesLayerBelow.batchDraw();
    this._updateShapesList();
    this._syncEffectRotationHandle();
  },

  _deselectShape() {
    this._clearCharacterMove?.();
    this._hideShapePopover?.();
    this._finishEffectRotation();
    this._clearEffectRotationHandle();
    if (!this._selectedShapeId) return;
    const shape = this._findShapeById(this._selectedShapeId);
    if (shape?.konvaShape) {
      shape.konvaShape.dash([]);
      if (isDirectionalEffect(shape)) shape.konvaShape.setAttr('sourceSelected', false);
      // Return shape to its correct layer
      this._returnShapeToOwnLayer(shape);
    }
    if (this._shapeTransformer) {
      this._shapeTransformer.nodes([]);
      this._shapeTransformer.visible(false);
    }
    this._selectedShapeId = null;
    this._shapesLayerBelow.batchDraw();
    this._shapesLayerAbove.batchDraw();
    this._updateShapesList();
  },

  _deleteShapeById(id, { save = true } = {}) {
    if (!this._canEditShape(this._findShapeById(id))) return;
    this._finishShapeAnimations?.();
    if (this._characterMove?.id === id) this._clearCharacterMove();
    for (const child of this._shapes.filter((shape) => shape.parentId === id)) this._deleteShapeById(child.id, { save: false });
    const index = this._shapes.findIndex((s) => s.id === id);
    if (index === -1) return;
    const shape = this._shapes[index];
    if (this._selectedShapeId === id) {
      this._clearEffectRotationHandle();
      if (this._shapeTransformer) {
        this._shapeTransformer.nodes([]);
        this._shapeTransformer.visible(false);
      }
      this._selectedShapeId = null;
    }
    if (this._editingShapeId === id) this._closeEditModal();
    shape.konvaShape.destroy();
    if (shape.konvaLabels) shape.konvaLabels.destroy();
    this._shapes.splice(index, 1);
    this._movementDistances?.remove(id);
    this._shapesLayerBelow.batchDraw();
    this._shapesLayerAbove.batchDraw();
    this._updateShapesList();
    if (save) this._save();
  },

  _commitShapeMove() {
    const shape = this._findShapeById(this._selectedShapeId);
    if (!shape) return;
    this._recreateLabels(shape);
    shape.konvaShape.dash([8, 4]); // keep selected appearance
    this._shapesLayerAbove.batchDraw();
    this._shapesLayerBelow.batchDraw();
    this._save();
  },

  _commitShapeResize() {
    const shape = this._findShapeById(this._selectedShapeId);
    if (!shape) return;
    if (['area-effect', 'character'].includes(shape.type)) return this._syncAreaEffectGeometry();
    const node = shape.konvaShape;
    const width = Math.max(MIN_SHAPE_DIMENSION, node.width() * node.scaleX());
    const height = Math.max(MIN_SHAPE_DIMENSION, node.height() * node.scaleY());
    node.width(width);
    node.height(height);
    node.scaleX(1);
    node.scaleY(1);
    this._recreateLabels(shape);
    node.dash([8, 4]);
    this._shapesLayerAbove.batchDraw();
    this._save();
  },

  _onKeyDown(event) {
    if (
      this._gridCalibration && event.key === 'Escape' &&
      event.target.closest('dialog[open]') === this.element.closest('dialog[open]')
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.cancelGridCalibration();
      return;
    }
    if (
      !event.target.closest('input, textarea, select, [contenteditable]') &&
      event.target.closest('dialog[open]') === this.element.closest('dialog[open]') &&
      this._onCharacterMoveKey?.(event)
    )
      return;
    if (
      event.key === 'Escape' &&
      this._pendingCharacterToken &&
      !event.target.closest('input, textarea, select, [contenteditable]') &&
      event.target.closest('dialog[open]') === this.element.closest('dialog[open]')
    ) {
      event.preventDefault();
      this._applyTool(this.playerValue ? 'drag' : 'select');
      return;
    }
    if (event.target.closest('input, textarea, select, [contenteditable], [data-controller~="toolbar-popover"]'))
      return;
    if (event.target.closest('dialog[open]') !== this.element.closest('dialog[open]')) return;
    if (event.key === 'Escape' && this._effectRotating) {
      event.preventDefault();
      this._finishEffectRotation({ cancel: true });
      return;
    }
    if (event.key === 'Escape' && this._pendingAreaEffect) {
      event.preventDefault();
      this._applyTool('select');
      return;
    }
    if (this._tool !== 'select' || !this._selectedShapeId) return;
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      const shape = this._findShapeById(this._selectedShapeId);
      if (!shape) return;
      if (confirm(`Delete ${shape.name}? (enter to confirm)`)) this._deleteShapeById(shape.id);
    }
  },

  _findShapeById(id) {
    return this._shapes?.find((s) => s.id === id) || null;
  },

  _getShapeLayer(shape) {
    return shape.aboveFog ? this._shapesLayerAbove : this._shapesLayerBelow;
  },

  _moveShapeToCorrectLayer(shape) {
    this._rebuildShapeZOrder();
  },

  _ensureShapeOnTop(shape) {
    if (this.playerValue) return;

    this._shapeTransformer.moveTo(this._shapesLayerAbove);
  },

  _returnShapeToOwnLayer(shape) {
    this._rebuildShapeZOrder();
  },

  _focusShapeControls(id) {
    requestAnimationFrame(() => {
      if (this.workspaceValue && this.hasSelectionToolbarTarget) {
        this.selectionToolbarTarget.querySelector('button')?.focus();
        return;
      }
      if (this._selectedShapeId === id && this.hasShapeColorTarget) this.shapeColorTarget.focus();
    });
  },

  _isTransformerHandleTarget(target) {
    if (!target || !this._shapeTransformer) return false;
    if (target === this._shapeTransformer) return true;
    return typeof target.getParent === 'function' && target.getParent() === this._shapeTransformer;
  },

  _recreateLabels(shape) {
    const wasVisible = shape.konvaLabels ? shape.konvaLabels.visible() : shape.showLabels !== false;
    if (shape.konvaLabels) shape.konvaLabels.destroy();
    const r = shape.konvaShape;
    const bounds = r.getAttr('tileFootprint')?.bounds || { x: 0, y: 0, width: r.width(), height: r.height() };
    shape.konvaLabels =
      shape.type === 'character'
        ? this._createCharacterTokenLabel(shape)
        : this._createShapeLabels(
            r.x() + bounds.x,
            r.y() + bounds.y,
            bounds.width,
            bounds.height,
            shape.name,
            shape.color
          );
    shape.konvaLabels.visible(wasVisible);
    shape.konvaLabels.offset(r.offset());
    // Add labels to the same layer as the shape (or above if selected)
    const targetLayer =
      this._selectedShapeId === shape.id && !this.playerValue ? this._shapesLayerAbove : this._getShapeLayer(shape);
    targetLayer.add(shape.konvaLabels);
  },

  _computeFill(shape) {
    if (!shape.fillColor || shape.fillColor === 'transparent') return 'transparent';
    return this._hexToRgba(shape.fillColor, shape.fillOpacity ?? SHAPE_DEFAULTS.fillOpacity);
  },

  _createShapeLabels(rx, ry, rw, rh, name, color) {
    const maxFont = 18,
      minFont = 8,
      gap = 2;
    const group = new Konva.Group({ listening: false, opacity: 0.9 });

    const measure = new Konva.Text({ text: name, fontSize: maxFont, fontFamily: 'Arial, sans-serif' });
    const tw = measure.width();
    measure.destroy();

    const hFont = tw > 0 && tw > rw ? Math.max(minFont, Math.min(maxFont, Math.floor(maxFont * (rw / tw)))) : maxFont;
    const vFont = tw > 0 && tw > rh ? Math.max(minFont, Math.min(maxFont, Math.floor(maxFont * (rh / tw)))) : maxFont;

    const textProps = (extra) => ({
      text: name,
      fill: color,
      fontFamily: 'Arial, sans-serif',
      stroke: 'black',
      strokeWidth: 0.5,
      strokeLinejoin: 'round',
      listening: false,
      wrap: 'none',
      ellipsis: true,
      ...extra
    });

    group.add(
      new Konva.Text(
        textProps({ x: rx, y: ry - hFont - gap, width: rw, height: hFont, fontSize: hFont, align: 'center' })
      )
    ); // top
    group.add(
      new Konva.Text(textProps({ x: rx, y: ry + rh + gap, width: rw, height: hFont, fontSize: hFont, align: 'center' }))
    ); // bottom
    group.add(
      new Konva.Text(
        textProps({
          x: rx - gap,
          y: ry + rh,
          width: rh,
          height: vFont,
          fontSize: vFont,
          align: 'center',
          rotation: -90
        })
      )
    ); // left
    group.add(
      new Konva.Text(
        textProps({ x: rx + rw + gap, y: ry, width: rh, height: vFont, fontSize: vFont, align: 'center', rotation: 90 })
      )
    ); // right
    return group;
  },

  _shapeModalFrame() {
    // UTMR's drawer-modal must remain inside the same live editor scope. Full
    // pages keep using the layout's shared modal frame.
    return (
      this.element.closest('dialog[data-drawer]')?.querySelector('turbo-frame#drawer-modal') ||
      document.getElementById('modal')
    );
  },

  _showEditModal(id, { tab = null } = {}) {
    if (!this._canEditShape(this._findShapeById(id)) || !this.hasShapeModalUrlValue) return;
    const modalFrame = this._shapeModalFrame();
    if (!modalFrame) return;

    this._editingShapeId = id;
    this._hideShapePopover?.();
    this._watchShapeModal(modalFrame);
    const url = new URL(this.shapeModalUrlValue, window.location.origin);
    url.searchParams.set('shape_id', id);
    if (tab) url.searchParams.set('tab', tab);
    modalFrame.src = url.href;
  },

  _watchShapeModal(modalFrame = this._shapeModalFrame()) {
    if (!modalFrame || (modalFrame === this._shapeModalFrameElement && this._shapeModalClosedHandler)) return;
    this._unwatchShapeModal();
    this._shapeModalFrameElement = modalFrame;
    this._shapeModalClosedHandler = () => {
      this._editingShapeId = null;
      this._unwatchShapeModal();
      this._refreshShapePopover?.();
    };
    modalFrame.addEventListener('modal:closed', this._shapeModalClosedHandler, { once: true });
  },

  _unwatchShapeModal() {
    this._shapeModalFrameElement?.removeEventListener('modal:closed', this._shapeModalClosedHandler);
    this._shapeModalFrameElement = null;
    this._shapeModalClosedHandler = null;
  },

  _refreshEditModal() {
    this._refreshShapePopover?.();
    if (!this.hasShapeEditModalTarget || this.element.dataset.konvaMapReady !== 'true') return;
    this._editingShapeId = this.shapeEditModalTarget.dataset.shapeId || this._editingShapeId;
    if (!this._editingShapeId) return;
    this._hideShapePopover?.();
    this._watchShapeModal();
    const shape = this._findShapeById(this._editingShapeId);
    if (!this._canEditShape(shape)) {
      const body = this.shapeEditModalTarget.querySelector('.modal-main');
      if (body) body.textContent = 'This layer is no longer available. Close this dialog to return to the map.';
      return;
    }

    if (this._tool !== 'select') this._showToolbar('shapes', 'select');
    if (this._selectedShapeId !== shape.id) this._selectShape(shape.id);
    const field = (name) => this.shapeEditModalTarget.querySelector(`[data-shape-field="${name}"]`);
    this._refreshCharacterEffects?.(shape, field);
    const fillOpacityPct = Math.round((shape.fillOpacity ?? SHAPE_DEFAULTS.fillOpacity) * 100);
    const hasFill = shape.type === 'character' || (shape.fillColor && shape.fillColor !== 'transparent');
    const isAreaEffect = shape.type === 'area-effect';
    this._refreshEditModalTitle();
    field('name').value = shape.name;
    field('name').readOnly = shape.type === 'character';
    // A restored modal URL may not include a shape_id; bind the live identity.
    this.shapeEditModalTarget.querySelectorAll('[data-shape-id]').forEach((node) => {
      node.dataset.shapeId = shape.id;
    });
    field('areaEffectFields').hidden = !isAreaEffect;
    field('radiusFields').hidden = !isAreaEffect || shape.effectType !== 'radius';
    field('cubeFields').hidden = !isAreaEffect || shape.effectType !== 'cube';
    field('directionalFields').hidden = !isAreaEffect || !isDirectionalEffect(shape);
    field('widthFields').hidden = !isAreaEffect || shape.effectType !== 'line';
    field('effectColor').value = shape.color;
    if (isAreaEffect) {
      field('effectType').value = shape.effectType[0].toUpperCase() + shape.effectType.slice(1);
    }
    if (isAreaEffect && shape.effectType === 'radius') {
      field('radius').value = shape.radiusFeet;
      field('radiusSize').textContent =
        `${shape.radiusFeet} ft radius · ${shape.radiusFeet * 2 + (this._findShapeById(shape.parentId)?.sizeFeet || 0)} × ${shape.radiusFeet * 2 + (this._findShapeById(shape.parentId)?.sizeFeet || 0)} ft square`;
    }
    if (isAreaEffect && shape.effectType === 'cube') {
      field('side').value = shape.sideFeet;
      field('sideSize').textContent = `${shape.sideFeet} × ${shape.sideFeet} ft square`;
    }
    if (isAreaEffect && isDirectionalEffect(shape)) {
      field('length').value = shape.lengthFeet;
      field('lengthSize').textContent =
        shape.effectType === 'cone'
          ? `${shape.lengthFeet} ft long · ${shape.lengthFeet} ft wide at the end`
          : `${shape.lengthFeet} ft`;
      if (shape.effectType === 'line') {
        field('width').value = shape.widthFeet;
        field('widthSize').textContent = `${shape.widthFeet} ft`;
      }
      field('directionDial').dataset.directionDialValueValue = mapAngleToBearing(shape.directionDegrees);
    }
    field('labels').textContent = shape.showLabels !== false ? 'Labels visible' : 'Labels hidden';
    field('labels').setAttribute('aria-pressed', shape.showLabels !== false);
    field('fog').textContent = shape.aboveFog !== false ? 'Hide under fog of war' : 'Show above fog of war';
    field('fog').setAttribute('aria-pressed', shape.aboveFog !== false);
    field('strokeWidth').value = shape.strokeWidth;
    field('strokeWidthLabel').textContent = `${shape.strokeWidth}px`;
    field('borderColor').value = shape.color;
    if (field('fillFields')) field('fillFields').hidden = shape.type === 'character';
    field('fillColor').value = shape.fillColor !== 'transparent' ? shape.fillColor : shape.color;
    field('fillOpacity').value = hasFill ? fillOpacityPct : 0;
    field('fillOpacity').disabled = false;
    field('fillOpacityLabel').textContent = `${hasFill ? fillOpacityPct : 0}%`;
  },

  _refreshEditModalTitle() {
    if (!this._editingShapeId || !this.hasShapeEditModalTarget) return;
    const shape = this._findShapeById(this._editingShapeId);
    if (!shape) return;
    const titleEl = this.shapeEditModalTarget.querySelector('.modal-title-h');
    if (titleEl) titleEl.textContent = `Edit: ${shape.name}`;
  },

  _closeEditModal() {
    if (!this.hasShapeEditModalTarget) return;
    window.modal?.hideModal();
    this._editingShapeId = null;
  },

  _updateShapesList() {
    if (!this.hasShapesListTarget) return;
    this._syncMobileControls?.();
    this._sortables?.forEach((sortable) => sortable.destroy());
    this._sortables = [];
    if (this._sortable) {
      this._sortable.destroy();
      this._sortable = null;
    }
    for (const target of this.shapesListTargets || [this.shapesListTarget]) this._renderShapesList(target);
    this._refreshShapePopover?.();
  },

  _renderShapesList(target) {
    const editableShapes = this._editableShapes();
    if (editableShapes.length === 0) {
      target.innerHTML = '<div class="text-muted small">No shapes drawn yet</div>';
      return;
    }

    const groups = groupedLayers(editableShapes).reverse();
    const row = (shape, index, linked = false) => {
      const sel = this._selectedShapeId === shape.id;
      const id = this._escapeHtml(shape.id);
      const name = this._escapeHtml(shape.name);
      return `<div id="game_map_layer_${id}${target.closest('dialog') ? '_dialog' : ''}" class="d-flex align-items-center gap-2 mb-1 p-1 rounded border ${sel ? 'border-light bg-dark' : 'border-primary'}"
          data-shape-id="${id}" data-action="click->konva-map#editShape">
        ${linked ? '' : '<span class="shape-drag-handle flex-shrink-0 opacity-50" style="cursor:grab" aria-hidden="true"><i class="fas fa-grip-vertical"></i></span>'}
        <button type="button" class="btn btn-sm border-0 text-light p-1 d-flex align-items-center gap-2 flex-grow-1 min-w-0 text-start"
            aria-label="Edit ${name}" aria-haspopup="dialog" aria-expanded="${sel}">
          <i class="fas ${shape.type === 'character' ? 'fa-user-circle' : shape.type === 'area-effect' ? 'fa-bullseye' : 'fa-square'} flex-shrink-0" style="color:${this._escapeHtml(shape.color)}" aria-hidden="true"></i>
          <span class="text-truncate flex-grow-1">${name}</span><i class="fas fa-pencil flex-shrink-0" aria-hidden="true"></i>
        </button>
        ${this.workspaceValue ? `<button type="button" class="btn btn-sm btn-outline-danger" data-action="click->konva-map#deleteShape" data-shape-id="${id}" aria-label="Delete ${name}"><i class="fa fa-trash" aria-hidden="true"></i></button>` : ''}
        ${
          linked
            ? ''
            : `<button type="button" class="btn btn-sm btn-outline-secondary game-map-mobile-only" data-action="click->konva-map#moveLayer" data-shape-id="${id}" data-direction="-1" aria-label="Move ${name} up" ${index === 0 ? 'disabled' : ''}>↑</button>
        <button type="button" class="btn btn-sm btn-outline-secondary game-map-mobile-only" data-action="click->konva-map#moveLayer" data-shape-id="${id}" data-direction="1" aria-label="Move ${name} down" ${index === groups.length - 1 ? 'disabled' : ''}>↓</button>`
        }
      </div>`;
    };
    target.innerHTML = groups
      .map(
        ({ shape, effects }, index) =>
          `<div data-layer-group="${this._escapeHtml(shape.id)}">${row(shape, index)}${
            effects.length
              ? `<div class="ms-3 ps-2 border-start" role="group" aria-label="Effects of ${this._escapeHtml(shape.name)}">${effects
                  .toReversed()
                  .map((effect) => row(effect, index, true))
                  .join('')}</div>`
              : ''
          }</div>`
      )
      .join('');

    const sortable = Sortable.create(target, {
      handle: '.shape-drag-handle',
      draggable: '[data-layer-group]',
      animation: 150,
      delayOnTouchOnly: true,
      delay: 150,
      touchStartThreshold: 6,
      onStart: () => this._hideShapePopover?.(),
      onEnd: (evt) => {
        this._onShapeReorder(evt);
        this._updateShapesList();
      }
    });
    this._sortables.push(sortable);
  },

  _onShapeReorder(evt) {
    const { oldIndex, newIndex } = evt;
    if (oldIndex === newIndex) return;

    const groups = groupedLayers(this._editableShapes()).reverse();
    if (oldIndex < 0 || newIndex < 0 || oldIndex >= groups.length || newIndex >= groups.length) return;
    const [moved] = groups.splice(oldIndex, 1);
    groups.splice(newIndex, 0, moved);
    const editable = flattenLayers(groups.reverse());
    this._shapes = this._shapes.map((shape) => (this._canEditShape(shape) ? editable.shift() : shape));

    this._rebuildShapeZOrder();
    this._save();
  },

  _rebuildShapeZOrder() {
    this._shapes = flattenLayers(groupedLayers(this._shapes));
    // Linked effects render immediately below their character in each fog layer.
    this._shapes.forEach((s) => {
      s.konvaShape.remove();
      if (s.konvaLabels) s.konvaLabels.remove();
    });

    this._shapes.forEach((s) => {
      const layer = this._getShapeLayer(s);
      layer.add(s.konvaShape);
      if (s.konvaLabels) layer.add(s.konvaLabels);
    });

    // Transformer always on top
    if (this._shapeTransformer) {
      this._shapeTransformer.moveToTop();
    }

    this._shapesLayerBelow.batchDraw();
    this._shapesLayerAbove.batchDraw();
  }
};
